import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import React from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import AppLink from '../../elements/AppLink';
import MobileNavBar from './MobileNavBar';
import event from '../../../utils/analytics/event';
import { NavBarContainer, Logo, MobileLogo, Links, SubscribeButton, LinkMenuSection, Link, ExternalLink } from './NavBar.styles';
/**
 * Styled nav component as part of the Phase One product requirements.
 *
 * This nav bar is NOT themeable; however, it uses AppLink, ExternalLink, Logo and Button which can be themed.
 *
 * In order to theme any of the
 *
 * ####Customizable items based on props:
 *
 * - `headerColor`: Header background color
 * - `menuColor`: Expanded menu background color
 * - `menuItemColor`: Menu item color
 * - `menuDividerColor`: Color for the horizontal dividers in the expanded menu
 * - `menuIconColor`: Color for the menu and X icons in the top right corner of the expanded menu
 * - `font`: Font for the nav bar
 * - `mobileHoverState`: Hover state for menu items on the expanded menu (tablet & mobile)
 * - `desktopHoverState`: Hover state for menu items on the header (desktop)
 * - `buttonText`: Subscribe button text
 *
 * To theme the button, AppLink, text elements, or any other internal elements, it is suggested that you theme them and wrap this component in a ThemeProvider.
 *
 * In order to add any product links to the nav bar, you'll want to pass an array to the `links` prop. The array should hold multiple objects in the form of {to: '', title:''} where `to` holds the route and `title` holds menu item label. Each item defaults to being an applink (inside of the application); however, if you would like to use an external link then add `external: true` to the object at hand. If you would like to any custom on-click functions per item click, you'll want to add a `onClick` key to the object at hand.
 *
 * ## Example Usage
 *
 * ```js
 * import { NavBar } from '@minerva-analytics/pantheon';
 *
 * const links = [
 *   {
 *     to: '/',
 *     title: 'Home',
 *   },
 *   {
 *     to: '/about',
 *     title: 'About Us',
 *   },
 *   {
 *     to: '/news',
 *     title: 'News',
 *   },
 *   {
 *     to: 'https://www.google.com',
 *     title: 'External',
 *     external: true,
 *   },
 * ];
 *
 * return (
 *   <NavBar
 *     links={links}
 *     font="Arial"
 *     mobileLogo="https://storage.googleapis.com/quickscope/assets/quickscope_favicon.png"
 *     logo="https://storage.googleapis.com/quickscope/assets/QuickScope_Logo.png"
 *     menuItemColor="white"
 *     headerColor="#094a6b"
 *     menuColor="#094a6b"
 *     menuIconColor="white"
 *     menuDividerColor="#2EF070"
 *     desktopHoverState={{
 *       textDecoration: `underline`,
 *     }}
 *     mobileHoverState={{
 *       backgroundColor: `#2CEFBC`,
 *     }}
 *   />
 * );
 * ```
 *
 * By default, NavBar uses following piwik events:
 *
 * - Nav item click event: `event(['click', 'nav-item', link.title]);`
 * - Subscribe button click event: `event(['click', 'subscribe-button', 'nav-header']);`
 * - Nav toggle event: `event(['click', 'toggle-nav']);`
 *
 * If an object is passed for the piwikEvents param, then 3 custom piwik events will be logged instead of the default options.
 *
 * `piwikEvents` must be passed in as such: `{navItemClick:[], subscribeClick:[], menuToggle:[]}`
 */

export var NavBar = function NavBar(_ref) {
  var logo = _ref.logo,
      mobileLogo = _ref.mobileLogo,
      links = _ref.links,
      menuColor = _ref.menuColor,
      headerColor = _ref.headerColor,
      menuItemColor = _ref.menuItemColor,
      font = _ref.font,
      menuIconColor = _ref.menuIconColor,
      menuDividerColor = _ref.menuDividerColor,
      mobileHoverState = _ref.mobileHoverState,
      desktopHoverState = _ref.desktopHoverState,
      piwikEvents = _ref.piwikEvents,
      _ref$buttonText = _ref.buttonText,
      buttonText = _ref$buttonText === void 0 ? 'Subscribe' : _ref$buttonText,
      rest = _objectWithoutPropertiesLoose(_ref, ["logo", "mobileLogo", "links", "menuColor", "headerColor", "menuItemColor", "font", "menuIconColor", "menuDividerColor", "mobileHoverState", "desktopHoverState", "piwikEvents", "buttonText"]);

  var location = useLocation();
  var history = useHistory();
  var pathname = location.pathname;
  var showSubscribe = pathname !== "/";

  var itemOnClick = function itemOnClick(link) {
    if (link.onClick) {
      link.onClick();
    }
  };

  return /*#__PURE__*/React.createElement(NavBarContainer, _extends({}, rest, {
    font: font,
    headerColor: headerColor
  }), /*#__PURE__*/React.createElement(AppLink, {
    to: "/"
  }, /*#__PURE__*/React.createElement(Logo, {
    src: logo,
    alt: "logo"
  }), /*#__PURE__*/React.createElement(MobileLogo, {
    src: mobileLogo,
    alt: "Logo_mobile"
  })), /*#__PURE__*/React.createElement(LinkMenuSection, null, /*#__PURE__*/React.createElement(Links, null, links.map(function (link) {
    var selected = pathname === link.to;
    return link.external ? /*#__PURE__*/React.createElement(ExternalLink, {
      selected: selected,
      key: link.title,
      href: link.to,
      target: "_blank",
      menuItemColor: menuItemColor,
      desktopHoverState: desktopHoverState,
      onClick: function onClick() {
        event(piwikEvents ? piwikEvents.navItemClick : ['click', 'nav-item', link.title]);
        itemOnClick(link);
      }
    }, link.title) : /*#__PURE__*/React.createElement(Link, {
      selected: selected,
      key: link.title,
      to: link.to,
      menuItemColor: menuItemColor,
      desktopHoverState: desktopHoverState,
      onClick: function onClick() {
        event(piwikEvents ? piwikEvents.navItemClick : ['click', 'nav-item', link.title]);
        itemOnClick(link);
      }
    }, link.title);
  })), showSubscribe && /*#__PURE__*/React.createElement(SubscribeButton, {
    onClick: function onClick() {
      history.push("/");
      event(piwikEvents ? piwikEvents.subscribeClick : ['click', 'subscribe-button', 'nav-header']);
    }
  }, buttonText), /*#__PURE__*/React.createElement(MobileNavBar, {
    menuColor: menuColor,
    menuIconColor: menuIconColor,
    menuItemColor: menuItemColor,
    links: links,
    menuDividerColor: menuDividerColor,
    mobileHoverState: mobileHoverState,
    piwikEvents: piwikEvents,
    buttonText: buttonText
  })));
};
NavBar.propTypes = {
  /** Product's logo (for desktop and tablet) */
  logo: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,

  /** Product's favicon logo (for mobile) */
  mobileLogo: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,

  /** An array of links that will be used for each menu item. The array should hold multiple objects in the form of {to: ``, title:``} where `to` holds the route and `title` holds menu item label. Each item defaults to being an applink (inside of the application); however, if you would like to use an external link then add `external: true` to the object at hand. */
  links: PropTypes.array.isRequired,

  /** Custom text for the subscribe button in the top right corner. Defaults to `Subscribe` if nothing is passed in. */
  buttonText: PropTypes.string,

  /** Background color for the nav header (prior to expansion) */
  headerColor: PropTypes.string,

  /** Background color for the nav when expanded */
  menuColor: PropTypes.string,

  /** Custom color for each menu item */
  menuItemColor: PropTypes.string,

  /** Custom color for the horizontal menu item divider when the nav is expanded. If a string is not passed into this, then it defaults to the value passed in for `menuItemColor`. */
  menuDividerColor: PropTypes.string,

  /** Custom font for the entire nav bar */
  font: PropTypes.string,

  /** Custom color for the menu icon and X icon in the top right corner of the expandable menu (on mobile). */
  menuIconColor: PropTypes.string,

  /** Custom hover state for menu items on the expanded menu (tablet & mobile). An object with any custom styles should be passed in to this prop. */
  mobileHoverState: PropTypes.object,

  /** Custom hover state for menu items on the header (desktop). An object with any custom styles should be passed in to this prop. */
  desktopHoverState: PropTypes.object,

  /** Custom piwik events that will be emitted when interactions with NavBar happens */
  piwikEvents: PropTypes.object
};
export default NavBar;